import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { Party, WizardState } from '../api';
import {
  Button,
  Card,
  ErrorBanner,
  Pill,
  Spinner,
  TextInput,
} from '../ui/primitives';

export function StepSelf({ wizard }: { wizard: WizardState }) {
  const qc = useQueryClient();
  const { data: parties, isLoading } = useQuery<Party[]>({
    queryKey: ['parties', 'self'],
    queryFn: () => api.get<Party[]>('/api/parties?role=self'),
  });
  const [displayName, setDisplayName] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: (name: string) =>
      api.post<Party>('/api/parties', { role: 'self', displayName: name }),
    onSuccess: () => {
      setDisplayName('');
      setLocalError(null);
      void qc.invalidateQueries();
    },
    onError: (err) => {
      if (err instanceof ApiError) setLocalError(err.message);
      else setLocalError(String(err));
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      api.patch<Party>(`/api/parties/${id}`, { displayName: name }),
    onSuccess: () => void qc.invalidateQueries(),
  });

  const self = parties?.[0] ?? null;

  return (
    <Card>
      <header className="mb-4">
        <h2 className="text-lg font-semibold">Step 1 — Your self party</h2>
        <p className="mt-1 text-caption text-ink-muted">
          This is you. Every margin computation ends up on this party&apos;s
          books. There can be exactly one self party. Name it whatever you want;
          your clients never see it.
        </p>
      </header>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-ink-muted">
          <Spinner /> loading…
        </div>
      )}

      {self ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Pill tone="green">exists</Pill>
            <span className="text-sm text-ink">{self.displayName}</span>
          </div>
          <SelfRenameForm
            key={self.id}
            initial={self.displayName}
            onSave={(name) => updateMut.mutate({ id: self.id, name })}
            saving={updateMut.isPending}
          />
        </div>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            const name = displayName.trim();
            if (!name) {
              setLocalError('Please enter a display name.');
              return;
            }
            createMut.mutate(name);
          }}
        >
          <TextInput
            label="Display name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Me, or your first name"
            hint="Only visible inside this app; your clients never see it."
            name="displayName"
          />
          {localError && <ErrorBanner>{localError}</ErrorBanner>}
          <div className="flex items-center gap-2">
            <Button type="submit" disabled={createMut.isPending}>
              {createMut.isPending ? <Spinner /> : 'Create self party'}
            </Button>
            {wizard.counts.uplineParties > 0 && (
              <span className="text-[11px] text-ink-muted">
                already created? refresh the page.
              </span>
            )}
          </div>
        </form>
      )}
    </Card>
  );
}

function SelfRenameForm({
  initial,
  onSave,
  saving,
}: {
  initial: string;
  onSave: (name: string) => void;
  saving: boolean;
}) {
  const [value, setValue] = useState(initial);
  const changed = value.trim() !== initial;
  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (changed) onSave(value.trim());
      }}
    >
      <TextInput
        label="Rename"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        name="rename"
      />
      <Button
        type="submit"
        variant="ghost"
        disabled={!changed || saving || value.trim().length === 0}
      >
        {saving ? <Spinner /> : 'Save'}
      </Button>
    </form>
  );
}
